import React from 'react';
import { ProductDesignPageHeader, ProductDesignSection, SwatchChip } from './ProductDesignDocPrimitives';
import { TOKEN_GROUPS } from './designTokens.generated';
import { PeakPrioritiesLogo } from '../../components/ui/PeakPrioritiesLogo';

/* ── shared local helpers ─────────────────────────────────────────── */

/** Small eyebrow label — the deck's wide-tracked caps. */
const CapsLabel: React.FC<{ tone?: 'label' | 'faint' | 'forest' | 'clay'; children: React.ReactNode }> = ({
  tone = 'label',
  children,
}) => {
  const color =
    tone === 'forest'
      ? 'text-[var(--p-forest)] dark:text-[var(--text-ter)]'
      : tone === 'clay'
        ? 'text-[var(--p-clay)]'
        : tone === 'faint'
          ? 'text-[var(--p-faint)]'
          : 'text-[var(--p-label)]';
  return (
    <div className={`mb-2 font-mono text-[13px] font-medium uppercase tracking-[0.2em] ${color}`}>{children}</div>
  );
};

/** A framed plate for showing a brand asset on a fixed light or dark surface,
 *  independent of the page's (daylight) chrome. */
const Plate: React.FC<{ tone: 'light' | 'dark'; children: React.ReactNode }> = ({ tone, children }) => (
  <div className="flex-1 min-w-[200px]">
    <CapsLabel>{tone === 'light' ? 'On light' : 'On dark'}</CapsLabel>
    <div
      className="flex min-h-[120px] items-center justify-center border border-[var(--p-hairline)] p-8"
      style={{ backgroundColor: tone === 'light' ? '#FFFFFF' : '#0B0F19' }}
    >
      {children}
    </div>
  </div>
);

/* ── Logo System ──────────────────────────────────────────────────── */

export const PrioritiesAILogoSystemPage: React.FC = () => (
  <>
    <ProductDesignPageHeader
      title="Logo system"
      eyebrow="Identity · marks"
      subtitle={
        <>
          Two marks, two jobs. The Peak Priorities logo is the company's. The six-bar mark is
          Priorities.ai's, the product. Both are shown here on light and dark plates.
        </>
      }
    />

    <ProductDesignSection
      title="The Peak Priorities logo"
      hint="The shield mark and the name in capitals. This is the current logo (confirmed 2026-10-01); it is the one the public site uses."
    >
      <div className="flex flex-wrap gap-4">
        <Plate tone="light">
          <PeakPrioritiesLogo height={36} className="text-[#3D4B5C]" />
        </Plate>
        <Plate tone="dark">
          <PeakPrioritiesLogo height={36} className="text-[#E8ECF4]" />
        </Plate>
      </div>
      <p className="mt-3 font-mono text-xs text-[var(--text-ter)]">
        src/components/ui/PeakPrioritiesLogo.tsx · slate #3D4B5C on light, paper #E8ECF4 on dark
      </p>
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[var(--text-sec)]">
        The logo is drawn in the page, so it takes one color and needs no separate reversed
        file. The shield on its own is the browser-tab icon and the installed-app icon for both
        the site and the app (<code className="font-mono text-[var(--mustard)]">public/favicon.svg</code>).
      </p>
    </ProductDesignSection>

    <ProductDesignSection
      title="The Priorities.ai mark"
      hint="Six bars, narrowing upward. It marks the product: the app's sidebar, and the Priorities.ai item in the site's top bar. Its mid-slate tones read on both light and dark surfaces."
    >
      <div className="flex flex-wrap gap-4">
        <Plate tone="light">
          <img src="/priorities-mark.svg" alt="Priorities.ai mark" style={{ height: 64 }} />
        </Plate>
        <Plate tone="dark">
          <img src="/priorities-mark.svg" alt="Priorities.ai mark" style={{ height: 64 }} />
        </Plate>
      </div>
      <p className="mt-3 font-mono text-xs text-[var(--text-ter)]">public/priorities-mark.svg</p>
    </ProductDesignSection>

    <ProductDesignSection title="Clear space & minimum size">
      <ul className="list-disc space-y-2 pl-6 text-sm leading-relaxed text-[var(--text-sec)]">
        <li>Keep clear space around either mark equal to the height of one bar of the six-bar mark (≈ 16px on standard density).</li>
        <li>Minimum height on screen: 24px for the six-bar mark, 16px for the Peak Priorities logo.</li>
        <li>Never crowd the lockup against dense controls — pair it with a <code className="font-mono text-[var(--mustard)]">--surface</code> plate.</li>
      </ul>
    </ProductDesignSection>

    <ProductDesignSection title="Do / Don't">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="border-l-[3px] border-[var(--p-forest)] pl-5">
          <CapsLabel tone="forest">Do</CapsLabel>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-[var(--text-sec)]">
            <li>Place the mark on flat <code className="font-mono text-[var(--mustard)]">--surface</code> / white plates.</li>
            <li>Use the SVG mark for any scalable context.</li>
          </ul>
        </div>
        <div className="border-l-[3px] border-[var(--p-clay)] pl-5">
          <CapsLabel tone="clay">Don't</CapsLabel>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-[var(--text-sec)]">
            <li>Don't set the slate logo on a dark ground; use the paper color there.</li>
            <li>Don't recolor or stretch the mark's steps.</li>
          </ul>
        </div>
      </div>
    </ProductDesignSection>
  </>
);

/* ── Brand Identity ───────────────────────────────────────────────── */

/** The four solid semantic accents, straight from the generated token sheet. */
const ACCENT_ROWS = TOKEN_GROUPS.filter((g) => g.id === 'accents')
  .flatMap((g) => g.rows)
  .filter((r) => r.dark.startsWith('#'));

/** One-word meanings for the accent cards; the full roles live on the token page. */
const ACCENT_MEANINGS: Record<string, string> = {
  '--slate': 'Structure',
  '--mustard': 'Priority',
  '--coral': 'Urgency',
  '--green': 'Execution',
};

export const PrioritiesAIBrandIdentityPage: React.FC = () => (
  <>
    <ProductDesignPageHeader
      title="Brand identity"
      eyebrow="Identity · color & lockups"
      subtitle="How the identity travels into product surfaces — the current accent tokens and lockup rules. Full ramps and what each palette is for live on the Token system and Colors pages."
    />

    <ProductDesignSection
      title="Canonical accents · product identity"
      hint="The four semantic accents, generated from src/index.css — each carries one meaning, in both modes. This is the color story every product surface speaks."
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {ACCENT_ROWS.map((r) => (
          <div key={r.token}>
            <CapsLabel>{ACCENT_MEANINGS[r.token] ?? r.role}</CapsLabel>
            <div className="mb-2 flex gap-1.5">
              <SwatchChip hex={r.dark} title={`${r.token} · dark`} />
              <SwatchChip hex={r.light} title={`${r.token} · light`} />
            </div>
            <code className="font-mono text-[13px] text-[var(--mustard)]">{r.token}</code>
          </div>
        ))}
      </div>
    </ProductDesignSection>

    <ProductDesignSection title="Lockup usage">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="border-l-[3px] border-[var(--p-forest)] pl-5">
          <CapsLabel tone="forest">Do</CapsLabel>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-[var(--text-sec)]">
            <li>Anchor lockups on flat neutral plates.</li>
            <li>Let mustard/coral appear only as small emphasis, never as brand fields.</li>
          </ul>
        </div>
        <div className="border-l-[3px] border-[var(--p-clay)] pl-5">
          <CapsLabel tone="clay">Don't</CapsLabel>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-[var(--text-sec)]">
            <li>Don't place lockups over ambiguous gradients behind production controls.</li>
            <li>Don't introduce chromatic blue as a UI action color — that's the structural accent's job.</li>
          </ul>
        </div>
      </div>
    </ProductDesignSection>

  </>
);

/* ── App Icons ────────────────────────────────────────────────────── */

const PRINCIPLES: { title: string; body: string }[] = [
  { title: 'Recognizable', body: 'The mark reads at 16px favicon scale — silhouette over detail.' },
  { title: 'Consistent', body: 'One mark across favicon, app icon, and touch icon; no per-size redraws.' },
  { title: 'Optimized', body: 'Ship SVG where supported, PNG ladders (16/32/180/192/512) elsewhere.' },
  { title: 'Accessible', body: 'Maintain contrast on both light and dark OS chrome; test on device.' },
];

export const PrioritiesAIAppIconsPage: React.FC = () => (
  <>
    <ProductDesignPageHeader
      title="Favicon & app icons"
      eyebrow="Identity · platform icons"
      subtitle="The shield from the Peak Priorities logo is the browser-tab icon and the installed-app icon, for the site and the app alike."
    />

    <ProductDesignSection title="Icon ladder" hint="One source, public/favicon.svg. The PNG sizes are drawn from it by scripts/buildAppIcons.mjs and wired through public/manifest.webmanifest.">
      <div className="flex flex-wrap items-end gap-6">
        {[16, 32, 48, 64].map((s) => (
          <div key={s} className="text-center">
            <div
              className="mb-2 flex items-center justify-center border border-[var(--p-hairline)] bg-white"
              style={{ width: 96, height: 96 }}
            >
              <img src="/favicon.svg" alt={`Mark at ${s}px`} style={{ width: s, height: s }} />
            </div>
            <span className="font-mono text-[13px] text-[var(--text-ter)]">{s}px</span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-sm text-[var(--text-sec)]">
        Files in <code className="font-mono text-[var(--mustard)]">public/</code>: the SVG for every browser size, and PNGs at{' '}
        <code className="font-mono text-[var(--mustard)]">180 (apple-touch) · 192 · 512</code>, plus maskable 192 and 512.
      </p>
    </ProductDesignSection>

    <ProductDesignSection title="Principles">
      <div className="border border-[var(--p-hairline)]">
        {PRINCIPLES.map(({ title, body }) => (
          <div key={title} className="flex items-stretch border-b border-[var(--p-hairline)] last:border-b-0">
            <span className="w-[3px] shrink-0 self-stretch bg-[var(--p-label)]" />
            <div className="flex flex-1 flex-col gap-1 py-4 pl-6 pr-4 sm:flex-row sm:items-baseline sm:gap-8">
              <span className="w-36 shrink-0 font-serif text-[16px] text-[var(--p-ink)]">{title}</span>
              <span className="font-body text-[14px] leading-relaxed text-[var(--p-ink-soft)]">{body}</span>
            </div>
          </div>
        ))}
      </div>
    </ProductDesignSection>
  </>
);
